import React, { useState, useEffect } from "react";
import styles from "./appointments-management.module.css";
import PageTitle from "../../components/screens/page-title";
import Table from "../../components/ui/table";
import Checkbox from "../../components/ui/checkbox";
import Dropdown from "../../components/ui/dropdown";
import Button from "../../components/ui/button";
import Input from "../../components/ui/input";
import { MyIcons } from "../../components/icons/my-icons";
import AssignAppointmentModal from "../../components/modals/AssignAppointmentModal";
import SambaFilePickerModal from "../../components/modals/SambaFilePickerModal";
import { getAppointmentCounts, getAppointmentsByTypeAndGroup, assignAppointment, getUsersActivosAndPartes, devolverCita } from "../../services/apiService";
import { showSuccessToast, showErrorToast } from "../../utils/notifications";
import { useNavigate } from "react-router-dom";
import usePageTitle from "../../hooks/usePageTitle";

const AppointmentsManagementPage = () => {
  usePageTitle('Gestión de Partes');
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  const [selectedFilters, setSelectedFilters] = useState({
    parteVisita: false,
    parteTrabajo: false,
    parteReclamacion: false,
    parteMediciones: false,
    parteFotos: false,
  });
  const [appointmentCounts, setAppointmentCounts] = useState({
    parteVisita: 0,
    parteTrabajo: 0,
    parteReclamacion: 0,
    parteMediciones: 0,
    parteFotos: 0,
  });
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState({ value: "TODOS", label: "Todos" });
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [selectedAppointmentForFiles, setSelectedAppointmentForFiles] = useState(null);
  const [usersWithPartes, setUsersWithPartes] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  const grupoTrabajoOptions = [
    { value: "TODOS", label: "Todos" },
    { value: "GRUPO_A", label: "Grupo A" },
    { value: "GRUPO_B", label: "Grupo B" },
  ];
  
  // Si no hay grupo seleccionado o es "TODOS", usar string vacío (no envía grupo a la API)
  const grupo = (selectedGroup?.value === "TODOS" || !selectedGroup?.value) ? "" : selectedGroup.value;

  const headers = [
    { label: "Expediente", key: "expedienteId" },
    { label: "Domicilio", key: "domicilioCliente" },
    { label: "Compañía", key: "compania" },
    { label: "Tipo", key: "companiaTipo" },
    { label: "Operario", key: "operarioNombre" },
    { label: "Encargado", key: "encargada" },
    { label: "Estado", key: "estadoExpediente" },
  ];

  // Fetch appointment counts on component mount
  useEffect(() => {
    const fetchAppointmentCounts = async () => {
      try {
        // Using empty grupo as shown in your API example
        const counts = await getAppointmentCounts(grupo);
        setAppointmentCounts(counts);
      } catch (err) {
        console.error("Error fetching appointment counts:", err);
        setError("Error al cargar los contadores de citas");
      }
    };

    fetchAppointmentCounts();
  }, [grupo]);

  // Fetch users with assigned partes on component mount
  useEffect(() => {
    const fetchUsersWithPartes = async () => {
      try {
        setLoadingUsers(true);
        const users = await getUsersActivosAndPartes();
        setUsersWithPartes(users);
      } catch (err) {
        console.error("Error fetching users with partes:", err);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsersWithPartes();
  }, []);

  // Fetch appointments when filters or group change
  useEffect(() => {
    const fetchAppointments = async () => {
      setLoading(true);
      setError(null);
      try {
        let allAppointments = [];
        const hasSelectedFilters = Object.values(selectedFilters).some(selected => selected);
        
        // Si no hay filtros seleccionados, traer todos los tipos
        if (!hasSelectedFilters) {
          // Traer todos los tipos cuando no hay filtros
          const visitAppointments = await getAppointmentsByTypeAndGroup("VISITA", grupo);
          const workAppointments = await getAppointmentsByTypeAndGroup("TRABAJO", grupo);
          const claimAppointments = await getAppointmentsByTypeAndGroup("RECLAMACION", grupo);
          const medicionesAppointments = await getAppointmentsByTypeAndGroup("MEDICIONES", grupo);
          const fotosAppointments = await getAppointmentsByTypeAndGroup("FOTOS", grupo);
          allAppointments = [...visitAppointments, ...workAppointments, ...claimAppointments, ...medicionesAppointments, ...fotosAppointments];
        } else {
          // Fetch for each selected filter
          if (selectedFilters.parteVisita) {
            const visitAppointments = await getAppointmentsByTypeAndGroup("VISITA", grupo);
            allAppointments = [...allAppointments, ...visitAppointments];
          }
          
          if (selectedFilters.parteTrabajo) {
            const workAppointments = await getAppointmentsByTypeAndGroup("TRABAJO", grupo);
            allAppointments = [...allAppointments, ...workAppointments];
          }
          
          if (selectedFilters.parteReclamacion) {
            const claimAppointments = await getAppointmentsByTypeAndGroup("RECLAMACION", grupo);
            allAppointments = [...allAppointments, ...claimAppointments];
          }

          if (selectedFilters.parteMediciones) {
            const medicionesAppointments = await getAppointmentsByTypeAndGroup("MEDICIONES", grupo);
            allAppointments = [...allAppointments, ...medicionesAppointments];
          }

          if (selectedFilters.parteFotos) {
            const fotosAppointments = await getAppointmentsByTypeAndGroup("FOTOS", grupo);
            allAppointments = [...allAppointments, ...fotosAppointments];
          }
        }

        setAppointments(allAppointments);
        console.log("Appointments loaded:", allAppointments);
        if (allAppointments.length > 0) {
          console.log("Sample appointment structure:", allAppointments[0]);
        }
      } catch (err) {
        console.error("Error fetching appointments:", err);
        setError("Error al cargar las citas");
        setAppointments([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, [selectedFilters, grupo]);

  const handleFilterChange = (filterKey) => {
    setSelectedFilters(prev => ({
      ...prev,
      [filterKey]: !prev[filterKey]
    }));
  };

  const handleClearFilters = () => {
    setSelectedFilters({
      parteVisita: false,
      parteTrabajo: false,
      parteReclamacion: false,
      parteMediciones: false,
      parteFotos: false,
    });
    setSelectedGroup({ value: "TODOS", label: "Todos" });
  };

  const handleAppointmentClick = (appointment, e) => {
    // Detect middle click or modifier (Ctrl/Cmd)
    try {
      const isMiddle = !!(e && (e.nativeEvent?.button === 1 || e.button === 1 || e.which === 2));
      const isModifier = !!(e && (e.ctrlKey || e.metaKey));
      
      if (isMiddle || isModifier) {
        // Open in new tab using window.open which respects browser behavior
        window.open(`/record/${appointment.expedienteId}`, '_blank');
        return;
      }
    } catch (err) {
      console.error("Error handling click event:", err);
    }
    
    // Navigate normally
    navigate(`/record/${appointment.expedienteId}`);
  };

  // Open assign appointment modal
  const handleOpenAssignModal = (appointmentId) => {
    console.log("Opening assign modal for appointment:", appointmentId);
    setSelectedAppointmentId(appointmentId);
    setIsAssignModalOpen(true);
  };

  // Handle appointment assignment
  const handleAssignAppointment = async (usuarioId, urgente = false) => {
    try {
      setLoading(true);
      await assignAppointment(selectedAppointmentId, usuarioId, urgente);
      showSuccessToast("Cita asignada correctamente");
      
      // Refresh appointments
      let allAppointments = [];
      const hasSelectedFilters = Object.values(selectedFilters).some(selected => selected);
      
      if (!hasSelectedFilters) {
        const visitAppointments = await getAppointmentsByTypeAndGroup("VISITA", grupo);
        const workAppointments = await getAppointmentsByTypeAndGroup("TRABAJO", grupo);
        const claimAppointments = await getAppointmentsByTypeAndGroup("RECLAMACION", grupo);
        const medicionesAppointments = await getAppointmentsByTypeAndGroup("MEDICIONES", grupo);
        const fotosAppointments = await getAppointmentsByTypeAndGroup("FOTOS", grupo);
        allAppointments = [...visitAppointments, ...workAppointments, ...claimAppointments, ...medicionesAppointments, ...fotosAppointments];
      } else {
        if (selectedFilters.parteVisita) {
          const visitAppointments = await getAppointmentsByTypeAndGroup("VISITA", grupo);
          allAppointments = [...allAppointments, ...visitAppointments];
        }
        
        if (selectedFilters.parteTrabajo) {
          const workAppointments = await getAppointmentsByTypeAndGroup("TRABAJO", grupo);
          allAppointments = [...allAppointments, ...workAppointments];
        }
        
        if (selectedFilters.parteReclamacion) {
          const claimAppointments = await getAppointmentsByTypeAndGroup("RECLAMACION", grupo);
          allAppointments = [...allAppointments, ...claimAppointments];
        }

        if (selectedFilters.parteMediciones) {
          const medicionesAppointments = await getAppointmentsByTypeAndGroup("MEDICIONES", grupo);
          allAppointments = [...allAppointments, ...medicionesAppointments];
        }

        if (selectedFilters.parteFotos) {
          const fotosAppointments = await getAppointmentsByTypeAndGroup("FOTOS", grupo);
          allAppointments = [...allAppointments, ...fotosAppointments];
        }
      }
      
      setAppointments(allAppointments);
      
      // Close modal
      setIsAssignModalOpen(false);
      setSelectedAppointmentId(null);
    } catch (error) {
      console.error("Error assigning appointment:", error);
      showErrorToast("Error al asignar la cita");
    } finally {
      setLoading(false);
    }
  };

  // Handle assign modal cancel
  const handleAssignCancel = () => {
    setIsAssignModalOpen(false);
    setSelectedAppointmentId(null);
  };

  // Open files modal
  const handleOpenFilesModal = (appointment) => {
    console.log("Opening files modal for appointment:", appointment);
    setSelectedAppointmentForFiles(appointment);
    setIsFileModalOpen(true);
  };

  // Close files modal
  const handleCloseFilesModal = () => {
    setIsFileModalOpen(false);
    setSelectedAppointmentForFiles(null);
  };

  const handleDevolverCita = async (appointment, e) => {
    e.stopPropagation();
    
    console.log("handleDevolverCita called with appointment:", appointment);
    
    // Confirmar acción
    if (!window.confirm(`¿Estás seguro de devolver la cita del expediente ${appointment.expedienteId}?`)) {
      console.log("User cancelled the action");
      return;
    }
    
    console.log("User confirmed, calling devolverCita with citaId:", appointment.citaId || appointment.id);
    
    try {
      await devolverCita(appointment.citaId || appointment.id);
      showSuccessToast("Cita devuelta correctamente");
      console.log("Cita devuelta successfully, reloading appointments...");
      
      // Recargar las citas después de devolver
      setLoading(true);
      try {
        let allAppointments = [];
        const hasSelectedFilters = Object.values(selectedFilters).some(selected => selected);
        
        if (!hasSelectedFilters) {
          const visitAppointments = await getAppointmentsByTypeAndGroup("VISITA", grupo);
          const workAppointments = await getAppointmentsByTypeAndGroup("TRABAJO", grupo);
          const claimAppointments = await getAppointmentsByTypeAndGroup("RECLAMACION", grupo);
          const medicionesAppointments = await getAppointmentsByTypeAndGroup("MEDICIONES", grupo);
          const fotosAppointments = await getAppointmentsByTypeAndGroup("FOTOS", grupo);
          allAppointments = [...visitAppointments, ...workAppointments, ...claimAppointments, ...medicionesAppointments, ...fotosAppointments];
        } else {
          if (selectedFilters.parteVisita) {
            const visitAppointments = await getAppointmentsByTypeAndGroup("VISITA", grupo);
            allAppointments = [...allAppointments, ...visitAppointments];
          }
          
          if (selectedFilters.parteTrabajo) {
            const workAppointments = await getAppointmentsByTypeAndGroup("TRABAJO", grupo);
            allAppointments = [...allAppointments, ...workAppointments];
          }
          
          if (selectedFilters.parteReclamacion) {
            const claimAppointments = await getAppointmentsByTypeAndGroup("RECLAMACION", grupo);
            allAppointments = [...allAppointments, ...claimAppointments];
          }

          if (selectedFilters.parteMediciones) {
            const medicionesAppointments = await getAppointmentsByTypeAndGroup("MEDICIONES", grupo);
            allAppointments = [...allAppointments, ...medicionesAppointments];
          }

          if (selectedFilters.parteFotos) {
            const fotosAppointments = await getAppointmentsByTypeAndGroup("FOTOS", grupo);
            allAppointments = [...allAppointments, ...fotosAppointments];
          }
        }
        
        setAppointments(allAppointments);
        console.log("Appointments reloaded successfully");
      } catch (err) {
        console.error("Error reloading appointments:", err);
      } finally {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error al devolver cita:", error);
      showErrorToast("Error al devolver la cita");
    }
  };

  const getRowColor = (appointment) => {
    // You can customize row colors based on state or other criteria
    switch (appointment.estadoExpediente) {
      case "PTE_ASIGNAR_VISITA":
        return { backgroundColor: "#fff3cd" }; // Light yellow
      case "COMPLETADO":
        return { backgroundColor: "#d4edda" }; // Light green
      default:
        return {};
    }
  };
  console.log("Rendering AppointmentsManagementPage with appointments:", appointments);
  return (
    <div className={styles.appointmentsManagementPage}>
      <PageTitle title="Gestión de Partes" showBackButton={false} />
      
      <div className={styles.contentContainer}>
        <div className={styles.leftColumn}>
          <div className={styles.filtersSection}>
            <h3>Filtros de Citas</h3>
            
            <div className={styles.groupSelector}>
              <Dropdown
                label="Grupo de Trabajo"
                options={grupoTrabajoOptions}
                selected={selectedGroup}
                onSelect={setSelectedGroup}
                style={{ marginBottom: "16px" }}
              />
            </div>

            <div className={styles.checkboxContainer}>
              <Checkbox
                label={`Parte Visita (${appointmentCounts.parteVisita})`}
                checked={selectedFilters.parteVisita}
                onChange={() => handleFilterChange('parteVisita')}
              />
              <Checkbox
                label={`Parte Trabajo (${appointmentCounts.parteTrabajo})`}
                checked={selectedFilters.parteTrabajo}
                onChange={() => handleFilterChange('parteTrabajo')}
              />
              <Checkbox
                label={`Parte Reclamación (${appointmentCounts.parteReclamacion})`}
                checked={selectedFilters.parteReclamacion}
                onChange={() => handleFilterChange('parteReclamacion')}
              />
              <Checkbox
                label={`Parte Mediciones (${appointmentCounts.parteMediciones})`}
                checked={selectedFilters.parteMediciones}
                onChange={() => handleFilterChange('parteMediciones')}
              />
              <Checkbox
                label={`Parte Fotos (${appointmentCounts.parteFotos})`}
                checked={selectedFilters.parteFotos}
                onChange={() => handleFilterChange('parteFotos')}
              />
            </div>

            <div className={styles.clearFiltersButton}>
              <Button 
                onClick={handleClearFilters}
                style={{ 
                  width: "100%", 
                  backgroundColor: "#f8f9fa", 
                  color: "#6c757d",
                  border: "1px solid #dee2e6"
                }}
              >
                Limpiar Filtros
              </Button>
            </div>
          </div>

          <div className={styles.partesSection}>
            <h3>Partes Asignados</h3>
            {loadingUsers ? (
              <div className={styles.loadingMessage}>
                Cargando usuarios...
              </div>
            ) : (
              <div className={styles.usersList}>
                {usersWithPartes.map((user) => (
                  <div key={user.usuarioId} className={styles.userCard}>
                    <div className={styles.userCardContent}>
                      <span className={styles.userName}>{user.nombre}</span>
                      <span className={styles.userPartes}>{user.partesAsignadas} partes</span>
                    </div>
                  </div>
                ))}
                {usersWithPartes.length === 0 && !loadingUsers && (
                  <div className={styles.emptyMessage}>
                    No hay usuarios con partes asignados
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className={styles.rightColumn}>
          {/* Search box */}
          <div className={styles.searchContainer}>
            <Input
              name="search"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Buscar en todos los campos..."
              isSearch={true}
            />
          </div>

          {/* Espacio entre buscador y listado */}
          <div style={{ height: "16px" }} />

          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}

          {loading ? (
            <div className={styles.loadingMessage}>
              Cargando citas...
            </div>
          ) : (
            <Table
            headers={headers}
            data={appointments.filter(app => {
              if (!searchValue || !searchValue.trim()) return true;
              
              const search = searchValue.toLowerCase();
              
              // Search across all fields
              return (
                (app.expedienteId && app.expedienteId.toString().toLowerCase().includes(search)) ||
                (app.domicilioCliente && app.domicilioCliente.toLowerCase().includes(search)) ||
                (app.compania && app.compania.toLowerCase().includes(search)) ||
                (app.companiaTipo && app.companiaTipo.toLowerCase().includes(search)) ||
                (app.operarioNombre && app.operarioNombre.toLowerCase().includes(search)) ||
                (app.encargada && app.encargada.toLowerCase().includes(search)) ||
                (app.estadoExpediente && app.estadoExpediente.toLowerCase().includes(search))
              );
            })}
            onPress={handleAppointmentClick}
            customRowStyle={getRowColor}
            emptyMessage="No hay citas disponibles con los filtros seleccionados"
            renderActions={(row) => (
              <div className={styles.actionButtons}>
                <MyIcons
                  name="eye"
                  width={24}
                  height={24}
                  color={"var(--black)"}
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log("Eye icon clicked for row:", row);
                    handleOpenFilesModal(row);
                  }}
                  style={{ cursor: 'pointer', marginRight: '8px' }}
                />
                <MyIcons
                  name="send"
                  width={24}
                  height={24}
                  color={"var(--black)"}
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log("Send icon clicked for row:", row);
                    handleOpenAssignModal(row.citaId || row.id || row.appointmentId);
                  }}
                  style={{ cursor: 'pointer', marginRight: '8px' }}
                />
                <MyIcons
                  name="goBack"
                  width={24}
                  height={24}
                  color={"#dc3545"}
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log("GoBack icon clicked for row:", row);
                    handleDevolverCita(row, e);
                  }}
                  style={{ cursor: 'pointer' }}
                />
              </div>
            )}
          />
          )}
        </div>
      </div>
      
      {/* Modal de asignación de cita */}
      <AssignAppointmentModal
        open={isAssignModalOpen}
        appointmentId={selectedAppointmentId}
        onAssign={handleAssignAppointment}
        onCancel={handleAssignCancel}
      />

      {/* Modal de visualización de archivos */}
      {isFileModalOpen && selectedAppointmentForFiles && (
        <SambaFilePickerModal
          isOpen={isFileModalOpen}
          onClose={handleCloseFilesModal}
          onFilesSelect={() => {}} // No selección, solo vista
          expId={selectedAppointmentForFiles.expedienteId}
          expedienteYear={Math.floor(selectedAppointmentForFiles.expedienteId / 10000) + 2000}
          multiple={false}
          initialPath={selectedAppointmentForFiles.pathArchivos || ""}
        />
      )}
    </div>
  );
};

export default AppointmentsManagementPage;
